import { invokeFunction } from '../api'
import { toApiRequestError } from '../errors'

export type FindingStatus = 'draft' | 'approved' | 'rejected'

export interface Finding {
  finding_id: string
  visit_id: string
  standard_id: string
  severity: 'low' | 'medium' | 'high'
  summary: string
  status: FindingStatus
  approved_at: string | null
  approved_by: string | null
  created_at: string
}

function asRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function asFindingStatus(value: unknown): FindingStatus {
  if (value === 'approved') return 'approved'
  if (value === 'rejected') return 'rejected'
  return 'draft'
}

function toFinding(value: unknown): Finding {
  if (!asRecord(value)) throw new Error('Malformed finding in server response')
  if (typeof value.finding_id !== 'string') throw new Error('Malformed finding in server response')
  if (typeof value.visit_id !== 'string') throw new Error('Malformed finding in server response')
  if (typeof value.standard_id !== 'string') throw new Error('Malformed finding in server response')
  if (typeof value.summary !== 'string') throw new Error('Malformed finding in server response')
  if (value.severity !== 'low' && value.severity !== 'medium' && value.severity !== 'high') {
    throw new Error('Malformed finding in server response')
  }

  return {
    finding_id: value.finding_id,
    visit_id: value.visit_id,
    standard_id: value.standard_id,
    severity: value.severity,
    summary: value.summary,
    status: asFindingStatus(value.status),
    approved_at: typeof value.approved_at === 'string' ? value.approved_at : null,
    approved_by: typeof value.approved_by === 'string' ? value.approved_by : null,
    created_at: typeof value.created_at === 'string' ? value.created_at : new Date(0).toISOString(),
  }
}

export async function getFindings(filters: { visitId?: string; status?: FindingStatus } = {}): Promise<Finding[]> {
  const search = new URLSearchParams()
  if (filters.visitId) search.set('visit_id', filters.visitId)
  if (filters.status) search.set('status', filters.status)
  const query = search.toString()

  const { data, error } = await invokeFunction<{ findings: unknown }>('findings', {
    method: 'GET',
    path: query ? `?${query}` : '',
  })

  if (error) throw toApiRequestError(error, 'Failed to load findings')
  if (!data || !Array.isArray(data.findings)) throw new Error('Malformed findings response from server')
  return data.findings.map(toFinding)
}

export async function getFinding(findingId: string): Promise<Finding> {
  const { data, error } = await invokeFunction<unknown>('findings', {
    method: 'GET',
    path: `/${findingId}`,
  })

  if (error) throw toApiRequestError(error, 'Failed to load finding')
  return toFinding(data)
}

export async function reviewFinding(findingId: string, status: 'approved' | 'rejected'): Promise<Finding> {
  const { data, error } = await invokeFunction<unknown>('findings', {
    method: 'PATCH',
    path: `/${findingId}`,
    body: { status },
  })

  if (error) throw toApiRequestError(error, `Failed to ${status} finding`)
  return toFinding(data)
}

export interface GenerateCapaInput {
  title?: string
  description?: string
  due_date?: string | null
  assigned_to_user_id?: string | null
  evidence_required?: boolean
}

export interface GeneratedCorrectiveAction {
  action_id: string
  finding_id: string | null
  visit_id: string | null
  title: string
  status: string
}

/**
 * AI/structured CAPA scaffold from a finding (#1855).
 * Backend: POST /findings/:id/corrective-actions:generate
 * Roles (server): admin | editor | superadmin; rejected findings denied.
 */
export async function generateCorrectiveActionFromFinding(
  findingId: string,
  input: GenerateCapaInput = {},
): Promise<GeneratedCorrectiveAction> {
  const { data, error } = await invokeFunction<unknown>('findings', {
    method: 'POST',
    path: `/${findingId}/corrective-actions:generate`,
    body: {
      ...(input.title ? { title: input.title } : {}),
      ...(input.description ? { description: input.description } : {}),
      ...(input.due_date !== undefined ? { due_date: input.due_date } : {}),
      ...(input.assigned_to_user_id !== undefined
        ? { assigned_to_user_id: input.assigned_to_user_id }
        : {}),
      ...(typeof input.evidence_required === 'boolean'
        ? { evidence_required: input.evidence_required }
        : {}),
    },
  })

  if (error) throw toApiRequestError(error, 'Failed to generate CAPA from finding')
  if (!asRecord(data) || typeof data.action_id !== 'string') {
    throw new Error('Malformed generate CAPA response from server')
  }

  return {
    action_id: data.action_id,
    finding_id: typeof data.finding_id === 'string' ? data.finding_id : null,
    visit_id: typeof data.visit_id === 'string' ? data.visit_id : null,
    title: typeof data.title === 'string' ? data.title : 'Corrective action',
    status: typeof data.status === 'string' ? data.status : 'open',
  }
}
